import { PureComponent, ReactNode } from 'react';

import qs from 'qs';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ErrorState } from 'src/components/generic-message';
import globalUrls from 'src/urls';
import authRequest from 'src/utils/fetch';

import LoadingState from './file-loading';

const messages = defineMessages({
  errorDescription: {
    id: 'frontbucket.repository.source.file.markupEditPreviewErrorDescription',
    description: `A message explaining to the user that there was error while rendering a preview of their edits to a markup file`,
    defaultMessage: 'We ran into issues trying to show you the preview.',
  },
  errorHeader: {
    id: 'frontbucket.repository.source.file.markupEditPreviewErrorHeader',
    description: `The header for a message explaining to the user that there was error while rendering a preview of their edits to a markup file`,
    defaultMessage: 'Unable to preview this file...',
  },
});

export type RenderProps = {
  markupAsRawHtml: string;
};

type Props = {
  content: string;
  markupType: string;
  render: (props: RenderProps) => ReactNode;
  repositoryUuid?: string;
};

type State = {
  error: boolean;
  isLoading: boolean;
  markupAsRawHtml: string | null | undefined;
};

export default class MarkupFileRenderer extends PureComponent<Props, State> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    markupType: 'markdown',
  };

  state: State = {
    error: false,
    isLoading: false,
    markupAsRawHtml: null,
  };

  componentDidMount() {
    this.fetchRenderedMarkup();
  }

  componentDidUpdate(prevProps: Props) {
    const { content, repositoryUuid } = this.props;
    if (
      content !== prevProps.content ||
      repositoryUuid !== prevProps.repositoryUuid
    ) {
      this.fetchRenderedMarkup();
    }
  }

  fetchRenderedMarkup = async () => {
    this.setState({ isLoading: true });

    try {
      const renderedMarkupResponse = await fetch(
        authRequest(globalUrls.api.internal.contentPreview(), {
          method: 'POST',
          body: qs.stringify({
            content: this.props.content,
            markup_type: this.props.markupType,
            repo_uuid: this.props.repositoryUuid,
          }),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        })
      );

      const { markup } = await renderedMarkupResponse.json();

      this.setState({
        error: false,
        isLoading: false,
        markupAsRawHtml: markup,
      });
    } catch {
      this.setState({
        error: true,
        isLoading: false,
      });
    }
  };

  render() {
    const { error, isLoading, markupAsRawHtml } = this.state;

    if (isLoading) {
      return <LoadingState />;
    }

    if (error) {
      return (
        <ErrorState
          header={<FormattedMessage {...messages.errorHeader} />}
          description={
            <FormattedMessage tagName="p" {...messages.errorDescription} />
          }
          onClick={this.fetchRenderedMarkup}
        />
      );
    }

    return markupAsRawHtml ? this.props.render({ markupAsRawHtml }) : null;
  }
}
